import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ActiveContext,
  CurrentContextResponse,
  KidExitChallenge,
  KidExitResult,
  clearActiveContext,
  enterKidModeRequest,
  exitKidModeMfaRequest,
  exitKidModeRequest,
  getActiveContext,
  switchStudentContext,
} from '../api/context';
import { ApiError } from '../../../adapters/api-client/api';
import { saveTokens } from '../../../adapters/api-client/tokenStorage';
import { logout } from './authSlice';

/**
 * Where the learner context used to live between page loads.
 *
 * No longer read: PRT-BR-P01-04 says a refresh never invents state, and a copy
 * in the browser is exactly that — it survived the learner being removed, and
 * it survived "Ver como padre" being undone on the server by nothing at all.
 * Still removed, so the child's name it holds does not outlive the change.
 */
const LEGACY_STORAGE_KEY = 'iqmaxx_ctx';

function dropLegacyStorage(): void {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Storage may be blocked; there is then nothing to remove either.
  }
}

interface ContextState {
  activeContext: ActiveContext | null;
  /**
   * GAP-S01-01 — this device is in the child's space. Only the server sets it
   * and only the re-authenticated exit clears it; a refresh reads it back.
   */
  kidMode: boolean;
  /**
   * Whether the server has answered for this session yet. Until it has, a
   * parent route renders nothing rather than flash the console at a child.
   */
  restored: boolean;
  /**
   * DEC-S2-S01-06 — the server could not be asked. Not an answer: `kidMode` still
   * means nothing, so neither the console nor the child's space opens on it.
   * The screens offer a retry instead.
   */
  restoreFailed: boolean;
  isLoading: boolean;
  error: string | null;
}

/** Why a kid-mode call failed, as the server said it — the screen picks the words. */
export interface KidModeFailure {
  code?: string;
}

const failureOf = (error: unknown): KidModeFailure => {
  const body = error instanceof ApiError ? error.body : undefined;
  return { code: typeof body?.code === 'string' ? body.code : undefined };
};

const isChallenge = (result: KidExitResult | KidExitChallenge): result is KidExitChallenge =>
  'challenge' in result;

/**
 * DEC-S2-S01-04 — the fresh Parent session replaces the stored one here, before
 * any reducer sees the exit, so no Parent read goes out until it is in place.
 */
const adoptSession = <T extends KidExitResult | KidExitChallenge>(result: T): T => {
  if (!isChallenge(result) && result.session) saveTokens(result.session);
  return result;
};

/**
 * Starts in the parent view until the server says otherwise (AC-16: the safe
 * fallback is "Me", never a remembered learner).
 */
const initialState: ContextState = {
  activeContext: null,
  kidMode: false,
  restored: false,
  restoreFailed: false,
  isLoading: false,
  error: null,
};

export const switchStudent = createAsyncThunk(
  'context/switchStudent',
  async (studentId: string, { rejectWithValue }) => {
    try {
      const response = await switchStudentContext(studentId);
      return response.activeContext;
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

/**
 * PRT-08-FR-07 / PRT-BR-P02-08 — read the active learner back from the server.
 *
 * Runs whenever a parent's session is (re)established. The server revalidates
 * ownership, so a learner who is no longer this parent's comes back as null.
 */
export const restoreContext = createAsyncThunk(
  'context/restore',
  async (): Promise<CurrentContextResponse> => getActiveContext(),
);

/**
 * Open the learner's space for one learner on this device. The server checks
 * ownership and consent and writes the lock; the shell follows what it answers.
 */
export const enterKidMode = createAsyncThunk<
  CurrentContextResponse,
  string,
  { rejectValue: KidModeFailure }
>('context/enterKidMode', async (studentId, { rejectWithValue }) => {
  try {
    return await enterKidModeRequest(studentId);
  } catch (error) {
    return rejectWithValue(failureOf(error));
  }
});

/**
 * Leave the learner's space with the Parent's password. A refusal leaves the
 * lock in place; a challenge means the code comes next, through
 * {@link exitKidModeMfa}.
 */
export const exitKidMode = createAsyncThunk<
  KidExitResult | KidExitChallenge,
  string,
  { rejectValue: KidModeFailure }
>('context/exitKidMode', async (password, { rejectWithValue }) => {
  try {
    return adoptSession(await exitKidModeRequest(password));
  } catch (error) {
    return rejectWithValue(failureOf(error));
  }
});

/** The authenticator code, for an account with TOTP. */
export const exitKidModeMfa = createAsyncThunk<
  KidExitResult,
  { session: string; code: string },
  { rejectValue: KidModeFailure }
>('context/exitKidModeMfa', async ({ session, code }, { rejectWithValue }) => {
  try {
    return adoptSession(await exitKidModeMfaRequest(session, code));
  } catch (error) {
    return rejectWithValue(failureOf(error));
  }
});

/**
 * "Ver como padre" — leave learner context on the server first.
 *
 * Clearing only the browser left the server's record pointing at the learner,
 * so the next refresh put the parent straight back into the child's view.
 * If the server cannot confirm, the screen keeps showing the learner it still
 * has: showing "parent view" over a server that disagrees is the same lie.
 */
export const returnToParentView = createAsyncThunk(
  'context/returnToParentView',
  async (_: void, { rejectWithValue }) => {
    try {
      await clearActiveContext();
      return null;
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

const contextSlice = createSlice({
  name: 'context',
  initialState,
  reducers: {
    /**
     * Forget the context locally, without touching the server. For the paths
     * where the session itself is ending — the server record belongs to the
     * account and is read back on the next sign-in.
     */
    clearContext: (state) => {
      state.activeContext = null;
      state.kidMode = false;
      state.restored = false;
      state.restoreFailed = false;
      state.isLoading = false;
      state.error = null;
      dropLegacyStorage();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(switchStudent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(switchStudent.fulfilled, (state, action: PayloadAction<ActiveContext>) => {
        state.isLoading = false;
        state.activeContext = action.payload;
      })
      .addCase(switchStudent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(restoreContext.pending, (state) => {
        state.restoreFailed = false;
      })
      .addCase(restoreContext.fulfilled, (state, action) => {
        state.activeContext = action.payload?.activeContext ?? null;
        state.kidMode = Boolean(action.payload?.kidMode);
        state.restored = true;
        state.restoreFailed = false;
        dropLegacyStorage();
      })
      .addCase(restoreContext.rejected, (state) => {
        // DEC-S2-S01-06: a failed read is not an answer. `kidMode` starts false,
        // so reading "restored" here after a reload put the Parent console in
        // front of a child on any transient 5xx. The learner never comes from
        // what the screen last showed (AC-16), and nothing else is decided:
        // `restored` stays false, so no shell opens until the server has said.
        state.activeContext = null;
        state.restored = false;
        state.restoreFailed = true;
      })
      .addCase(enterKidMode.fulfilled, (state, action) => {
        state.activeContext = action.payload.activeContext;
        state.kidMode = Boolean(action.payload.kidMode);
        state.restored = true;
      })
      .addCase(exitKidMode.fulfilled, (state, action) => {
        // A challenge changes nothing: the lock holds until the code is in.
        if (isChallenge(action.payload)) return;
        state.activeContext = action.payload.activeContext;
        state.kidMode = Boolean(action.payload.kidMode);
      })
      .addCase(exitKidModeMfa.fulfilled, (state, action) => {
        state.activeContext = action.payload.activeContext;
        state.kidMode = Boolean(action.payload.kidMode);
      })
      .addCase(returnToParentView.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(returnToParentView.fulfilled, (state) => {
        state.isLoading = false;
        state.activeContext = null;
      })
      .addCase(returnToParentView.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Every sign-out path — the menu, settings, the console, an expired
      // session — ends in this action, so the learner leaves with it rather
      // than each caller having to remember a second dispatch.
      .addCase(logout, (state) => {
        state.activeContext = null;
        state.kidMode = false;
        state.restored = false;
        state.restoreFailed = false;
        state.isLoading = false;
        state.error = null;
        dropLegacyStorage();
      });
  },
});

export const { clearContext } = contextSlice.actions;
export default contextSlice.reducer;
